import type { TFunction } from 'i18next';
import { ApiError } from 'utils/api-client';

/**
 * Translate an API error/message code into a localized string.
 *
 * Lookup order:
 *   1. `messages.errors.<code>` (or `messages.success.<code>` for success kind)
 *   2. Caller-provided `fallback` (a LOCALIZED string the call site chose)
 *   3. ApiError's `fallbackMessage` (raw English string from the backend)
 *   4. Generic `messages.errors.GENERIC`
 *
 * The caller's localized `fallback` deliberately wins over the backend's English
 * `fallbackMessage`: on an unmapped or infrastructure error (bare 502/503/504)
 * the backend string is raw English, which would otherwise leak to a Greek user.
 * When the caller passes no fallback, the backend string still beats the generic.
 *
 * `params` from the ApiError are passed to i18next for `{{key}}` interpolation.
 *
 * Use this everywhere instead of reading `err.message` directly. That keeps
 * unmapped codes from leaking raw English strings while still rendering
 * something useful during the migration.
 */

interface TranslateOptions {
  /** Override the fallback string (e.g. a context-specific message). */
  fallback?: string;
  /** Treat the code as a success message rather than an error. */
  kind?: 'error' | 'success';
  /**
   * i18next context for event-type-aware messages — resolves
   * `messages.errors.<code>_baptism` / `_wedding_baptism` when defined,
   * falling back to the base code. Pass `sessionUser?.eventType`.
   */
  context?: string;
}

function namespaceForKind(kind: 'error' | 'success'): string {
  return kind === 'success' ? 'messages.success' : 'messages.errors';
}

export function translateApiMessage(
  err: unknown,
  t: TFunction,
  options: TranslateOptions = {},
): string {
  const { fallback, kind = 'error', context } = options;
  const ns = namespaceForKind(kind);
  const generic = t('messages.errors.GENERIC', { defaultValue: 'Something went wrong.' });

  if (err instanceof ApiError) {
    const key = `${ns}.${err.code}`;
    const interpolation = (err.params ?? {}) as Record<string, unknown>;
    return t(key, {
      ...interpolation,
      ...(context ? { context } : {}),
      defaultValue: fallback || err.fallbackMessage || generic,
    });
  }

  if (err instanceof Error && err.message) {
    return fallback || err.message;
  }

  return fallback || generic;
}

/**
 * Translate a successful API response's message code, if any.
 * Pass the parsed JSON body — handles missing `messageCode` by returning the
 * fallback string.
 */
export function translateApiSuccess(
  body: { messageCode?: number | string; message?: string; params?: Record<string, unknown> },
  t: TFunction,
  fallback?: string,
  context?: string,
): string {
  if (body?.messageCode != null) {
    return t(`messages.success.${body.messageCode}`, {
      ...(body.params ?? {}),
      ...(context ? { context } : {}),
      defaultValue: body.message || fallback || '',
    });
  }
  return body?.message || fallback || '';
}
